import { useState } from "react";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { TextInput } from "@freightsimple/ui-core/Controls/TextInput";
import { isValidEmail } from "@freightsimple/ui-core/Helpers/isValidEmail";

/**
 * Asked once per browser, right after opening the carrier's shared login link:
 * who is using the portal? Self-asserted name + work email, cached long-term
 * and baked into the session for the audit trail. Styling is raw here (App/
 * infrastructure splash, like CarrierSignedOut).
 */
export function CarrierIdentityPrompt({
  onSubmit,
}: {
  onSubmit: (name: string, email: string) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState(false);

  const canSubmit =
    !submitting && name.trim() !== "" && isValidEmail(email.trim());

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setFailed(false);
    try {
      await onSubmit(name, email);
    } catch {
      setFailed(true);
      setSubmitting(false);
    }
    // On success the auth state moves to authed and this screen unmounts —
    // no local state to reset.
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-light-background">
      <img src="/logo.png" alt="FreightSimple" className="h-14 w-14" />
      <div className="text-xl font-bold text-foreground">Carrier Portal</div>
      <div className="max-w-sm text-center text-sm text-content-secondary">
        Before you continue, tell us who you are. This is shown on the changes
        and messages you make in the portal.
      </div>
      <div
        className="flex w-72 flex-col gap-3"
        onKeyDown={(event) => {
          if (event.key === "Enter") void submit();
        }}
      >
        <TextInput
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Your name"
          width="full"
          data-testid="identity-name-input"
        />
        <TextInput
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="Work email"
          width="full"
          data-testid="identity-email-input"
        />
        {failed && (
          <div className="text-center text-xs text-red">
            We couldn&apos;t sign you in with those details. Please check them
            and try again.
          </div>
        )}
        <Button
          primary
          onClick={() => void submit()}
          disabled={!canSubmit}
          disabledMessage="Enter your name and work email to continue"
          loading={submitting}
          data-testid="identity-continue-button"
        >
          Continue
        </Button>
      </div>
    </div>
  );
}
